import Dexie, { type EntityTable } from 'dexie'

import type {
  ConflictHistory,
  LocalEntity,
  OutboxMutation,
  SyncDiagnostic,
} from '../../domain/sync'
import type { ExportManifest, MemoryLineString, TripArchiveState } from '../../domain/memories'
import type { ChallengeContentRole } from '../../config/tripChallenges'
import { migrateTripTruthData } from './tripTruthMigration'

export type AppSetting = {
  key: string
  schemaRevision: number
  mode: 'demo' | 'supabase'
  lastCursor: number
  lastSuccessfulSyncAt: string | null
  lastPendingAlertAt: string | null
  protectedCacheState: 'open' | 'locked'
}

export type CachedAssetMeta = {
  key: string
  category: 'thumbnail' | 'map_style' | 'public_asset'
  lastAccessedAt: string
  byteEstimate: number
  evictionEligible: boolean
}

export type ProtectedCacheRecord = {
  key: string
  category: string
  ciphertext: string
  locked: boolean
  updatedAt: string
}

export type LocalBlob = {
  key: string
  entityId: string
  kind: 'original' | 'processed' | 'thumbnail'
  bytes: ArrayBuffer
  mimeType: string
  confirmedRemote: boolean
  createdAt: string
}

export type PositionSampleRecord = {
  id: string
  tripId: string
  memberId: string
  displayName: string
  latitude: number
  longitude: number
  accuracy: number | null
  capturedAt: string
  liveUntil: string
  expiresAt: string
  mode: 'point' | 'live'
}

export type DerivedTrailPoint = {
  id: string
  tripId: string
  memberId: string
  latitude: number
  longitude: number
  occurredAt: string
}

export type LocalChallengeRecord = {
  id: string
  tripId: string
  kind: 'trip_secret' | 'daily'
  productDay: string | null
  title: string
  detail: string
  category: 'humor' | 'observation_photo' | 'culture' | 'gastronomy' | 'mini_adventure' | 'social_interaction'
  safetyNote: string
  createdBy: string
  createdAt: string
  manuallyRevealedAt: string | null
  contentRole?: ChallengeContentRole
}

export type LocalChallengeAssignment = {
  key: string
  challengeId: string
  tripId: string
  memberId: string
  assignedAt: string
}

export type LocalNotificationRecord = {
  id: string
  tripId: string
  memberId: string
  kind: 'agenda_changed' | 'position_request' | 'sync_pending' | 'poll' | 'deadline' | 'challenge' | 'reveal' | 'photo' | 'interaction'
  importance: 'important' | 'social'
  title: string
  body: string
  deepLink: string
  createdAt: string
  readAt: string | null
  deliveryState: 'in_app' | 'push_pending' | 'push_sent' | 'push_failed'
}

export type MemoryRouteRecord = {
  tripId: string
  geoJson: MemoryLineString
  inputSummary: {
    derivedPositions: number
    pins: number
    photos: number
    checkIns: number
    segments: number
    rawPositions: 0
  }
  generatedAt: string
  archiveVisible: boolean
}

export type TripArchiveRecord = TripArchiveState

export type ExportManifestRecord = ExportManifest & { id: string }

export class CaravanDb extends Dexie {
  entities!: EntityTable<LocalEntity, 'key'>
  outbox!: EntityTable<OutboxMutation, 'mutationId'>
  histories!: EntityTable<ConflictHistory, 'id'>
  settings!: EntityTable<AppSetting, 'key'>
  cachedAssets!: EntityTable<CachedAssetMeta, 'key'>
  diagnostics!: EntityTable<SyncDiagnostic, 'id'>
  protectedCache!: EntityTable<ProtectedCacheRecord, 'key'>
  blobs!: EntityTable<LocalBlob, 'key'>
  positionSamples!: EntityTable<PositionSampleRecord, 'id'>
  derivedTrail!: EntityTable<DerivedTrailPoint, 'id'>
  challenges!: EntityTable<LocalChallengeRecord, 'id'>
  challengeAssignments!: EntityTable<LocalChallengeAssignment, 'key'>
  notifications!: EntityTable<LocalNotificationRecord, 'id'>
  memoryRoutes!: EntityTable<MemoryRouteRecord, 'tripId'>
  tripArchives!: EntityTable<TripArchiveRecord, 'tripId'>
  exportManifests!: EntityTable<ExportManifestRecord, 'id'>

  constructor(name: string) {
    super(name)
    this.version(1).stores({
      entities: '&key, id, tripId, entityType, syncState, updatedAtDevice, [entityType+id]',
      outbox:
        '&mutationId, entityId, tripId, entityType, state, createdAtDevice, nextAttemptAt, [state+nextAttemptAt]',
      histories: '&id, entityId, entityType, recordedAt',
      settings: '&key',
      cachedAssets: '&key, category, lastAccessedAt, evictionEligible',
      diagnostics: '&id, createdAt, phase, entityType, outcome',
      protectedCache: '&key, category, locked, updatedAt',
      blobs: '&key, entityId, kind, confirmedRemote, createdAt',
    })
    this.version(2).stores({
      entities: '&key, id, tripId, entityType, syncState, updatedAtDevice, [entityType+id]',
      outbox:
        '&mutationId, entityId, tripId, entityType, state, createdAtDevice, nextAttemptAt, [state+nextAttemptAt]',
      histories: '&id, entityId, entityType, recordedAt',
      settings: '&key',
      cachedAssets: '&key, category, lastAccessedAt, evictionEligible',
      diagnostics: '&id, createdAt, phase, entityType, outcome',
      protectedCache: '&key, category, locked, updatedAt',
      blobs: '&key, entityId, kind, confirmedRemote, createdAt',
      positionSamples: '&id, tripId, memberId, capturedAt, expiresAt, mode',
      derivedTrail: '&id, tripId, memberId, occurredAt',
    })
    this.version(3).stores({
      entities: '&key, id, tripId, entityType, syncState, updatedAtDevice, [entityType+id]',
      outbox:
        '&mutationId, entityId, tripId, entityType, state, createdAtDevice, nextAttemptAt, [state+nextAttemptAt]',
      histories: '&id, entityId, entityType, recordedAt',
      settings: '&key',
      cachedAssets: '&key, category, lastAccessedAt, evictionEligible',
      diagnostics: '&id, createdAt, phase, entityType, outcome',
      protectedCache: '&key, category, locked, updatedAt',
      blobs: '&key, entityId, kind, confirmedRemote, createdAt',
      positionSamples: '&id, tripId, memberId, capturedAt, expiresAt, mode',
      derivedTrail: '&id, tripId, memberId, occurredAt',
      challenges: '&id, tripId, kind, productDay, createdAt',
      challengeAssignments: '&key, challengeId, tripId, memberId, [challengeId+memberId]',
    })
    this.version(4).stores({
      entities: '&key, id, tripId, entityType, syncState, updatedAtDevice, [entityType+id]',
      outbox:
        '&mutationId, entityId, tripId, entityType, state, createdAtDevice, nextAttemptAt, [state+nextAttemptAt]',
      histories: '&id, entityId, entityType, recordedAt',
      settings: '&key',
      cachedAssets: '&key, category, lastAccessedAt, evictionEligible',
      diagnostics: '&id, createdAt, phase, entityType, outcome',
      protectedCache: '&key, category, locked, updatedAt',
      blobs: '&key, entityId, kind, confirmedRemote, createdAt',
      positionSamples: '&id, tripId, memberId, capturedAt, expiresAt, mode',
      derivedTrail: '&id, tripId, memberId, occurredAt',
      challenges: '&id, tripId, kind, productDay, createdAt',
      challengeAssignments: '&key, challengeId, tripId, memberId, [challengeId+memberId]',
      notifications: '&id, tripId, memberId, importance, createdAt, readAt, [memberId+createdAt]',
    })
    this.version(5).stores({
      entities: '&key, id, tripId, entityType, syncState, updatedAtDevice, [entityType+id]',
      outbox:
        '&mutationId, entityId, tripId, entityType, state, createdAtDevice, nextAttemptAt, [state+nextAttemptAt]',
      histories: '&id, entityId, entityType, recordedAt',
      settings: '&key',
      cachedAssets: '&key, category, lastAccessedAt, evictionEligible',
      diagnostics: '&id, createdAt, phase, entityType, outcome',
      protectedCache: '&key, category, locked, updatedAt',
      blobs: '&key, entityId, kind, confirmedRemote, createdAt',
      positionSamples: '&id, tripId, memberId, capturedAt, expiresAt, mode',
      derivedTrail: '&id, tripId, memberId, occurredAt',
      challenges: '&id, tripId, kind, productDay, createdAt',
      challengeAssignments: '&key, challengeId, tripId, memberId, [challengeId+memberId]',
      notifications: '&id, tripId, memberId, importance, createdAt, readAt, [memberId+createdAt]',
      memoryRoutes: '&tripId, generatedAt, archiveVisible',
      tripArchives: '&tripId, status, archivedAt',
      exportManifests: '&id, tripId, archiveVersion, generatedAt',
    })
    this.version(6).stores({
      entities: '&key, id, tripId, entityType, syncState, updatedAtDevice, [entityType+id]',
      outbox:
        '&mutationId, entityId, tripId, entityType, state, createdAtDevice, nextAttemptAt, [state+nextAttemptAt]',
      histories: '&id, entityId, entityType, recordedAt',
      settings: '&key',
      cachedAssets: '&key, category, lastAccessedAt, evictionEligible',
      diagnostics: '&id, createdAt, phase, entityType, outcome',
      protectedCache: '&key, category, locked, updatedAt',
      blobs: '&key, entityId, kind, confirmedRemote, createdAt',
      positionSamples: '&id, tripId, memberId, capturedAt, expiresAt, mode',
      derivedTrail: '&id, tripId, memberId, occurredAt',
      challenges: '&id, tripId, kind, productDay, createdAt',
      challengeAssignments: '&key, challengeId, tripId, memberId, [challengeId+memberId]',
      notifications: '&id, tripId, memberId, importance, createdAt, readAt, [memberId+createdAt]',
      memoryRoutes: '&tripId, generatedAt, archiveVisible',
      tripArchives: '&tripId, status, archivedAt',
      exportManifests: '&id, tripId, archiveVersion, generatedAt',
    }).upgrade(migrateTripTruthData)
  }
}

export function createCaravanDb(name = 'caravan-morocco-mvp'): CaravanDb {
  return new CaravanDb(name)
}
